'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var dragAndDrop = require('../utilities/drag-and-drop.js');

/**
 * Drop hook for making an HTML element a dropzone.
 *
 * @example
 * const ref = useRef<HTMLDivElement | null>(null);
 * const { dropped, entered, over } = useDrop({ format: 'custom', onDropped: (data, e, format) => {}, ref });
 * <div ref={ref}>Drop</div>
 *
 * @param options UseDropOptions<T>
 * @returns UseDropReturn
 */ const useDrop = (options)=>{
    const { delay =1000 , disabled =false , format , onDropped , ref  } = options;
    const htmlElement = ref?.current;
    const dragEnterTarget = React.useRef(null);
    const [dropped, setDropped] = React.useState(false);
    const [entered, setEntered] = React.useState(false);
    const [over, setOver] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(()=>{
        setMounted(true);
    }, []);
    React.useEffect(()=>{
        if (disabled || !mounted || !htmlElement || !htmlElement.addEventListener) {
            return;
        }
        const typesMatchesFormat = (types)=>{
            if (Array.isArray(format)) {
                const formats = new Set(format.map((f)=>f.toLowerCase()));
                return types.some((type)=>formats.has(type));
            }
            return types.includes(format.toLowerCase());
        };
        const resetEnterOver = ()=>{
            setEntered(false);
            setOver(false);
            dragEnterTarget.current = null;
        };
        const onDragEnter = (e)=>{
            e.preventDefault();
            const matches = typesMatchesFormat(e.dataTransfer?.types || []);
            if (matches) {
                dragEnterTarget.current = e.target;
                setEntered(true);
                htmlElement.ondragenter?.(e);
            }
        };
        const onDragLeave = (e)=>{
            const matches = typesMatchesFormat(e.dataTransfer?.types || []);
            if (matches && e.target === dragEnterTarget.current) {
                e.preventDefault();
                htmlElement.ondragleave?.(e);
                resetEnterOver();
            }
        };
        const onDragOver = (e)=>{
            e.preventDefault();
            const matches = typesMatchesFormat(e.dataTransfer?.types || []);
            if (matches) {
                setOver(true);
                htmlElement.ondragover?.(e);
            }
        };
        const resetDropped = ()=>{
            setTimeout(()=>{
                setDropped(false);
            }, delay);
        };
        const getData = (e, format)=>{
            const data = e.dataTransfer?.getData(format);
            if (data !== undefined && data !== '') {
                try {
                    const isString = dragAndDrop.getIsStringDragAndDropFormat(format);
                    const parsed = isString ? data : JSON.parse(data);
                    onDropped(parsed, e, format);
                } catch (error) {
                    console.error(error);
                    // returning the unparsed string data is better than nothing
                    onDropped(data, e, format);
                }
            }
        };
        const onData = (e)=>{
            Array.isArray(format) ? format.map((format)=>getData(e, format)) : getData(e, format);
            resetEnterOver();
            resetDropped();
        };
        const onDrop = (e)=>{
            e.preventDefault();
            const matches = typesMatchesFormat(e.dataTransfer?.types || []);
            if (matches) {
                setDropped(true);
                onData(e);
                htmlElement.ondrop?.(e);
            }
        };
        htmlElement.addEventListener('dragenter', onDragEnter);
        htmlElement.addEventListener('dragover', onDragOver);
        htmlElement.addEventListener('dragleave', onDragLeave);
        htmlElement.addEventListener('drop', onDrop);
        return ()=>{
            htmlElement.removeEventListener('dragenter', onDragEnter);
            htmlElement.removeEventListener('dragover', onDragOver);
            htmlElement.removeEventListener('dragleave', onDragLeave);
            htmlElement.removeEventListener('drop', onDrop);
        };
    }, [
        delay,
        disabled,
        format,
        htmlElement,
        mounted,
        onDropped
    ]);
    return {
        dropped,
        entered,
        over
    };
};

exports.useDrop = useDrop;
//# sourceMappingURL=use-drop.js.map
